// Helper type for CSS class strings or arrays of strings
type SlotClassDefinition = string | string[]

// Helper type for variant values (single or array)
type VariantValue<T> = T | T[]

// Define all known configurable slots for the Checkbox component.
//	Look at the Checkbox component for more details.
//	node_modules\@nuxt\ui\dist\runtime\components\Checkbox.vue (all props.ui.<prop> are available)
interface CheckboxSlots {
	root?: SlotClassDefinition
	container?: SlotClassDefinition
	base?: SlotClassDefinition
	indicator?: SlotClassDefinition
	icon?: SlotClassDefinition
	wrapper?: SlotClassDefinition
	label?: SlotClassDefinition
	description?: SlotClassDefinition
}
// Define the structure for Checkbox variants and their possible states
interface CheckboxVariants {
	color?: {
		primary?: Partial<CheckboxSlots>
		secondary?: Partial<CheckboxSlots>
		success?: Partial<CheckboxSlots>
		info?: Partial<CheckboxSlots>
		warning?: Partial<CheckboxSlots>
		error?: Partial<CheckboxSlots>
		neutral?: Partial<CheckboxSlots>
	}
	variant?: {
		list?: Partial<CheckboxSlots>
		card?: Partial<CheckboxSlots>
	}
	indicator?: {
		start?: Partial<CheckboxSlots>
		end?: Partial<CheckboxSlots>
		hidden?: Partial<CheckboxSlots>
	}
	size?: {
		xs?: Partial<CheckboxSlots>
		sm?: Partial<CheckboxSlots>
		md?: Partial<CheckboxSlots>
		lg?: Partial<CheckboxSlots>
		xl?: Partial<CheckboxSlots>
	}
	required?: {
		true?: Partial<CheckboxSlots> | string
	}
	disabled?: {
		true?: Partial<CheckboxSlots> | string
	}
	checked?: {
		true?: Partial<CheckboxSlots> | string
	}
}

export interface ExtendedCheckboxThemeConfig {
	slots?: Partial<CheckboxSlots>
	variants?: Partial<CheckboxVariants>
	compoundVariants?: Array<
		{
			// String-based variants: value or array of values
			//	Use 'NonNullable' to ensure that the variant is not null or undefined
			//	due to the "?" operators of CheckboxVariants properties
			size?: VariantValue<keyof NonNullable<CheckboxVariants['size']>>
			color?: VariantValue<keyof NonNullable<CheckboxVariants['color']>>
			variant?: VariantValue<keyof NonNullable<CheckboxVariants['variant']>>
			indicator?: VariantValue<keyof NonNullable<CheckboxVariants['indicator']>>

			// Boolean-based variants
			required?: boolean
			disabled?: boolean
			checked?: boolean
		} & {
			// 'class' property is mandatory for each compound variant item
			class: Partial<CheckboxSlots>
		}
	>
	defaultVariants?: {
		size?: keyof NonNullable<CheckboxVariants['size']>
		color?: keyof NonNullable<CheckboxVariants['color']>
		variant?: keyof NonNullable<CheckboxVariants['variant']>
		indicator?: keyof NonNullable<CheckboxVariants['indicator']>
		required?: boolean
		disabled?: boolean
		checked?: boolean
	}
}

export default {
	slots: {
		root: 'relative flex items-start',
		container: 'flex items-center',
		base: 'rounded-sm ring ring-inset ring-accented overflow-hidden focus-visible:outline-2 focus-visible:outline-offset-2',
		indicator: 'flex items-center justify-center size-full text-inverted',
		icon: 'shrink-0 size-full',
		wrapper: 'w-full',
		label: 'block font-medium text-default',
		description: 'text-muted'
	},
	variants: {
		color: {
			primary: {
				base: 'focus-visible:outline-primary',
				indicator: 'bg-primary'
			},
			secondary: {
				base: 'focus-visible:outline-secondary',
				indicator: 'bg-secondary'
			},
			success: {
				base: 'focus-visible:outline-success',
				indicator: 'bg-success'
			},
			info: {
				base: 'focus-visible:outline-info',
				indicator: 'bg-info'
			},
			warning: {
				base: 'focus-visible:outline-warning',
				indicator: 'bg-warning'
			},
			error: {
				base: 'focus-visible:outline-error',
				indicator: 'bg-error'
			},
			neutral: {
				base: 'focus-visible:outline-inverted',
				indicator: 'bg-inverted'
			}
		},
		variant: {
			list: {
				root: ''
			},
			card: {
				root: 'border border-(--ui-border-muted) rounded-lg'
			}
		},
		indicator: {
			start: {
				root: 'flex-row',
				wrapper: 'ms-2'
			},
			end: {
				root: 'flex-row-reverse',
				wrapper: 'me-2'
			},
			hidden: {
				base: 'sr-only',
				wrapper: 'text-center'
			}
		},
		size: {
			xs: {
				base: 'size-3',
				container: 'h-4',
				wrapper: 'text-xs'
			},
			sm: {
				base: 'size-3.5',
				container: 'h-4',
				wrapper: 'text-xs'
			},
			md: {
				base: 'size-4',
				container: 'h-5',
				wrapper: 'text-sm'
			},
			lg: {
				base: 'size-4.5',
				container: 'h-5',
				wrapper: 'text-sm'
			},
			xl: {
				base: 'size-5',
				container: 'h-6',
				wrapper: 'text-base'
			}
		},
		required: {
			true: {
				label: 'after:content-[\'*\'] after:ms-0.5 after:text-error'
			}
		},
		disabled: {
			true: {
				base: 'cursor-not-allowed opacity-75',
				label: 'cursor-not-allowed opacity-75',
				description: 'cursor-not-allowed opacity-75'
			}
		},
		checked: {
			true: ''
		}
	},
	compoundVariants: [
		{
			size: 'xs',
			variant: 'card',
			class: {
				root: 'p-2.5'
			}
		},
		{
			size: 'sm',
			variant: 'card',
			class: {
				root: 'p-3'
			}
		},
		{
			size: 'md',
			variant: 'card',
			class: {
				root: 'p-3.5'
			}
		},
		{
			size: 'lg',
			variant: 'card',
			class: {
				root: 'p-4'
			}
		},
		{
			size: 'xl',
			variant: 'card',
			class: {
				root: 'p-4.5'
			}
		},
		{
			color: 'primary',
			variant: 'card',
			class: {
				root: 'has-data-[state=checked]:border-primary'
			}
		},
		{
			color: 'neutral',
			variant: 'card',
			class: {
				root: 'has-data-[state=checked]:border-inverted'
			}
		},
		{
			variant: 'card',
			disabled: true,
			class: {
				root: 'cursor-not-allowed opacity-75'
			}
		}
	],
	defaultVariants: {
		size: 'md',
		color: 'primary',
		variant: 'list',
		indicator: 'start'
	}
} satisfies ExtendedCheckboxThemeConfig
